/**
 * 组队大厅（扩展② 双向选择）：未满员队伍公开展示缺口与准入档，
 * 学生按"与我互补度"排序浏览，一键申请（open 直通 / approval 待审批）。
 * myScore 由后端用匹配引擎对"我"单候选人打分——和"开始匹配"同一套引擎、同一个口径。
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { joinPolicyLabel, skillName } from '@crossmatch/shared';

import { teamsApi } from '../api/teams.js';
import type { HallCardDto } from '../api/types.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { useHallQuery } from '../hooks/queries.js';
import { qk } from '../lib/queryKeys.js';
import { schoolFullLabel } from '../lib/format.js';
import { toast } from '../store/toast.js';

function MyStatusTag({ status }: { status: HallCardDto['myStatus'] }): ReactNode {
  switch (status) {
    case 'requested':
      return <Tag tone="primary">待审批</Tag>;
    case 'accepted':
      return <Tag tone="ok">已加入</Tag>;
    case 'invited':
      return <Tag tone="primary">被邀请</Tag>;
    case 'declined':
      return <Tag tone="muted">曾被拒</Tag>;
    default:
      return null;
  }
}

/**
 * 大厅内容面板（不含 page-head）：独立拆出，供「组队大厅」页和「我的队伍 → 组队大厅 Tab」复用。
 * 查询、申请 mutation、赛事码过滤全部自包含。
 */
export function TeamHallPanel() {
  const hallQuery = useHallQuery();
  const queryClient = useQueryClient();
  const [eventCode, setEventCode] = useState('');

  const join = useMutation({
    mutationFn: (teamId: string) => teamsApi.join(teamId),
    onSuccess: (data) => {
      invalidate();
      const policy = data?.team?.joinPolicy;
      toast.success(
        policy === 'open' ? '已加入队伍' : '申请已提交',
        policy === 'open' ? '你现在是队伍成员了' : '等待队长在「我的队伍」里审批',
      );
    },
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hall'] });
    void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
  };

  if (hallQuery.isPending) {
    return <Loading label="正在读取大厅…" rows={3} />;
  }
  if (hallQuery.isError) {
    return <ErrorState error={hallQuery.error} title="读取组队大厅失败" onRetry={() => void hallQuery.refetch()} />;
  }

  const all = hallQuery.data.cards;
  const keyword = eventCode.trim().toUpperCase();
  const cards = keyword.length === 0 ? all : all.filter((card) => card.eventCode === keyword);

  return (
    <>
      <div className="row-tight">
        <input
          className="input"
          style={{ maxWidth: 220 }}
          value={eventCode}
          placeholder="按赛事码过滤，如 EV2026"
          onChange={(event) => setEventCode(event.target.value.toUpperCase())}
          aria-label="按赛事码过滤大厅"
        />
        <span className="muted small">{cards.length} 支队伍</span>
      </div>

      {cards.length === 0 ? (
        <EmptyState
          icon="◇"
          title="大厅里暂时没有可加入的队伍"
          description="队长建队后默认进入大厅（仅邀请档除外）。换个赛事码看看，或去「我的队伍」自己建一支。"
        />
      ) : (
        <div className="grid-2">
          {cards.map((card) => (
            <div key={card.teamId} className="card">
              <div className="row-tight">
                <strong>{card.teamName}</strong>
                <Tag tone="muted">{joinPolicyLabel(card.joinPolicy)}</Tag>
                <MyStatusTag status={card.myStatus} />
                {card.myScore !== null ? <Tag tone="primary">与我互补 {card.myScore}</Tag> : null}
              </div>
              <div className="muted small">
                队长 {card.ownerName} · {schoolFullLabel(card.school)} · 已确认 {card.acceptedCount} 人
                {card.eventCode !== null ? ` · 赛事 ${card.eventCode}` : ''}
              </div>
              {card.skills.length > 0 ? (
                <div className="row-tight">
                  <span className="small muted">招募：</span>
                  {card.skills.map((skill) => (
                    <Tag key={skill}>{skillName(skill)}</Tag>
                  ))}
                </div>
              ) : null}
              {card.missing.length > 0 ? (
                <div className="row-tight">
                  <span className="small muted">缺口：</span>
                  {card.missing.slice(0, 5).map((skill) => (
                    <Tag key={skill} tone="danger">
                      {skillName(skill)}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {card.note.length > 0 ? <div className="small wrap-anywhere">{card.note}</div> : null}
              <div className="row-tight">
                {card.myStatus === 'none' ? (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={join.isPending && join.variables === card.teamId}
                    onClick={() => join.mutate(card.teamId)}
                  >
                    {card.joinPolicy === 'open' ? '立即加入' : '申请加入'}
                  </Button>
                ) : null}
                {card.myScore === null && card.myStatus === 'none' ? (
                  <span className="field-hint">完善「我的画像」技能后可看互补度</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** 组队大厅独立页：page-head + 复用的内容面板（同一块内容也嵌在「我的队伍 → 组队大厅 Tab」）。 */
export function TeamHallPage() {
  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">组队大厅</h1>
          <p className="page-sub">
            双向选择：队伍公开缺口和准入档，你按「与我互补度」挑队申请。免审核队申请即入队，需审批队等队长通过。
          </p>
        </div>
      </div>
      <TeamHallPanel />
    </div>
  );
}
